import { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import pdfDataCache from '../documents/pdfDataCache';

// Types
export const CharacteristicType = {
  id: '',
  phenomenontime: '',
  resulttime: '',
  procedure: '',
  observedproperty: '',
  classifiedresult_domain: '',
  classifiedResult: null,
  remarks: '',
  created_at: null,
  updated_at: null,
  indicator: null,
  link_id: '',
  document_id: ''
};

// Hook for fetching characteristics
export const useCharacteristics = (param = {}) => {
  const [characteristics, setCharacteristics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCharacteristics = async () => {
      try {
        // Define cache keys
        const documentId = param.document_id || 'global';
        const tabName = 'characteristics';
        const extraKey = JSON.stringify(param);
        
        // Check cache first
        const cachedData = pdfDataCache.get(documentId, tabName, extraKey);
        
        if (cachedData) {
          setCharacteristics(cachedData);
          setLoading(false);
          return;
        }
        
        let query = supabase
          .from('document_characteristic')
          .select(`
            *,
            classified_result:document_domainvalueslookup!document_characteristic_classifiedResult_fkey(domain_value_id, description), 
            indicator_info:document_domainvalueslookup!document_characteristic_indicator_fkey(domain_value_id, technical_name, description)
          `);

        // Filter by link_id if provided
        if (param.link_id) {
          query = query.eq('link_id', param.link_id);
        }

        // Filter by document_id if provided
        if (param.document_id) {
          query = query.eq('document_id', param.document_id);
        }

        // Filter by indicator if provided
        if (param.indicator) {
          query = query.eq('indicator', param.indicator);
        }

        // Filter by classifiedResult if provided
        if (param.classifiedResult) {
          query = query.eq('classifiedResult', param.classifiedResult);
        }

        const { data, error } = await query;
        
        if (error) throw error;
        
        // Store in cache
        pdfDataCache.set(documentId, tabName, data, extraKey);
        
        setCharacteristics(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCharacteristics();
  }, [param]);

  return { characteristics, loading, error, setCharacteristics };
};

// Helper function to fetch a single characteristic by ID
export const getCharacteristicById = async (id) => {
  // Check cache first
  const cachedData = pdfDataCache.get('global', 'characteristic_by_id', id);
  if (cachedData) {
    return cachedData;
  }
  
  const { data, error } = await supabase
    .from('document_characteristic')
    .select(`
      *,
      classified_result:document_domainvalueslookup!document_characteristic_classifiedResult_fkey(*),
      indicator_info:document_domainvalueslookup!document_characteristic_indicator_fkey(*)
    `)
    .eq('id', id)
    .single();

  if (error) throw error;
  
  // Cache the result
  pdfDataCache.set('global', 'characteristic_by_id', data, id);
  
  return data;
};

// Helper function to create a new characteristic
export const createCharacteristic = async (characteristicData) => {
  const { data, error } = await supabase
    .from('document_characteristic')
    .insert([characteristicData])
    .select();

  if (error) throw error;
  
  // Invalidate relevant caches
  if (characteristicData.document_id) {
    pdfDataCache.clearTab(characteristicData.document_id, 'characteristics');
  }
  if (characteristicData.link_id) {
    pdfDataCache.clearTab('global', 'characteristics_by_link_' + characteristicData.link_id);
  }
  
  return data[0];
};

// Helper function to update an existing characteristic
export const updateCharacteristic = async (id, updates) => {
  const { data, error } = await supabase
    .from('document_characteristic')
    .update(updates)
    .eq('id', id)
    .select();

  if (error) throw error;
  
  // Invalidate relevant caches
  if (data[0]) {
    pdfDataCache.clearTab('global', 'characteristic_by_id');
    if (data[0].document_id) {
      pdfDataCache.clearTab(data[0].document_id, 'characteristics');
    }
    if (data[0].link_id) {
      pdfDataCache.clearTab('global', 'characteristics_by_link_' + data[0].link_id);
    }
  }
  
  return data[0];
};

// Helper function to delete a characteristic
export const deleteCharacteristic = async (id) => {
  // First get the characteristic to know which caches to invalidate
  const characteristic = await getCharacteristicById(id);
  
  const { error } = await supabase
    .from('document_characteristic')
    .delete()
    .eq('id', id);

  if (error) throw error;
  
  // Invalidate relevant caches
  pdfDataCache.clearTab('global', 'characteristic_by_id');
  if (characteristic && characteristic.document_id) {
    pdfDataCache.clearTab(characteristic.document_id, 'characteristics');
  }
  if (characteristic && characteristic.link_id) {
    pdfDataCache.clearTab('global', 'characteristics_by_link_' + characteristic.link_id);
  }
  
  return true;
};

// Helper function to get characteristics for a specific link
export const getCharacteristicsByLinkId = async (linkId) => {
  // Check cache first
  const cachedData = pdfDataCache.get('global', 'characteristics_by_link_' + linkId, '');
  if (cachedData) {
    return cachedData;
  }
  
  const { data, error } = await supabase
    .from('document_characteristic')
    .select(`
      *,
      classified_result:document_domainvalueslookup!document_characteristic_classifiedResult_fkey(*),
      indicator_info:document_domainvalueslookup!document_characteristic_indicator_fkey(*)
    `)
    .eq('link_id', linkId);

  if (error) throw error;
  
  // Cache the result
  pdfDataCache.set('global', 'characteristics_by_link_' + linkId, data, '');
  
  return data;
};

// Helper function to get characteristics by document id
export const getCharacteristicsByDocumentId = async (documentId) => {
  // Check cache first
  const cachedData = pdfDataCache.get(documentId, 'characteristics', '');
  if (cachedData) {
    return cachedData;
  }
  
  const { data, error } = await supabase
    .from('document_characteristic')
    .select(`
      *,
      classified_result:document_domainvalueslookup!document_characteristic_classifiedResult_fkey(*),
      indicator_info:document_domainvalueslookup!document_characteristic_indicator_fkey(*)
    `)
    .eq('document_id', documentId);

  if (error) throw error;
  
  // Cache the result
  pdfDataCache.set(documentId, 'characteristics', data, '');
  
  return data;
};

// Helper function to invalidate all characteristic caches
export const invalidateCharacteristicCache = (documentId = null) => {
  if (documentId) {
    pdfDataCache.clearTab(documentId, 'characteristics');
  } else {
    pdfDataCache.clearTab('global', 'characteristic_by_id');
    // For a more complete invalidation, we should clear all caches
    pdfDataCache.clear();
  }
};
